import styles from "./add-edit-user-role.module.css";
import React, { useEffect, useState } from "react";
import { Input, Select, Form, Row, Col, Upload, Button, Switch } from "antd";
import { PhoneOutlined, MailOutlined, UploadOutlined } from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import CustomDrawer from "../../../../common/custom-drawer/custom-drawer";
import CustomButton from "../../../../common/custom-button/custom-button";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import {
  addUserRole,
  getSuperAdminPrivileges,
  updateUserRole,
} from "../../../../services/user_role_management";
import CustomTable from "../../../../common/custom-table/custom-table";

const permissionColumns = [
  {
    title: "View",
    key: "can_view",
  },
  {
    title: "Add",
    key: "can_add",
  },
  {
    title: "Edit",
    key: "can_edit",
  },
  {
    title: "Delete",
    key: "can_delete",
  },
];
const AddEditUserRole = ({ open, onClose, userRoleData }) => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [fileName, setFileName] = useState("");

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      full_name: "",
      profile_picture: null,
      phone_number: "",
      email_address: "",
    },
  });

  useEffect(() => {
    getSuperAdminPrivilegeList();
    resetFormFields();
  }, [open]);

  const getSuperAdminPrivilegeList = async () => {
    setLoading(true);
    try {
      const response = await getSuperAdminPrivileges();
      if (response.status) {
        const consolidated_privileges = response?.data?.data?.privileges?.map(
          (privilege) => ({
            can_add: 1,
            can_delete: 1,
            can_edit: 1,
            can_view: 1,
            name: privilege?.label,
            key: privilege?.name,
          })
        );

        const merged = consolidated_privileges.map(itemA => {
          const itemB = userRoleData?.privileges?.find(item => item.key === itemA.key);
          if (itemB) {
            return {
              ...itemA,
              can_add: itemB.can_add?.toString() ? itemB.can_add : itemA.can_add,
              can_delete: itemB.can_delete?.toString() ? itemB.can_delete : itemA.can_delete,
              can_edit: itemB.can_edit?.toString() ? itemB.can_edit : itemA.can_edit,
              can_view: itemB.can_view?.toString() ? itemB.can_view : itemA.can_view
            };
          }
          return itemA;
        });

        setTableData(userRoleData?.privileges?.length? merged: consolidated_privileges);
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const resetFormFields = () => {
    reset({
      email_address: userRoleData?.email_address ?? "",
      full_name: userRoleData?.metadata?.full_name ?? "",
      phone_number: userRoleData?.metadata?.phone_number ?? "",
      profile_picture: null,
    });

    setFileName(
      userRoleData?.metadata?.profile_picture
        ? `${userRoleData?.metadata?.full_name?.toLowerCase()} profile.png`
        : ""
    );
  };

  const handleSwitchChange = (index, type, checked) => {
    const updatedData = [...tableData];
    updatedData[index][type] = checked ? 1 : 0;
    setTableData(updatedData);
  };

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      let response = null;
      const formData = new FormData();

      formData.append("full_name", values.full_name);
      formData.append("phone_number", values.phone_number);
      formData.append("email_address", values.email_address);

      if (values.profile_picture) {
        formData.append("profile_picture", values.profile_picture);
      }

      formData.append("privileges", JSON.stringify(tableData));
      const isEditing = userRoleData && Object.keys(userRoleData).length > 0;

      response = isEditing
        ? await updateUserRole(userRoleData.id, formData)
        : await addUserRole(formData);

      if (response?.data?.status) {
        toast.success(
          `User Role ${isEditing ? "Updated" : "Added"} Successfully!`
        );
      } else {
        toast.error("Something went wrong: Invalid API response");
      }
      onClose();
    } catch (error) {
      console.error("Caught error:", error);
      toast.error(error?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <span style={{ color: "rgba(102, 112, 133, 1)" }}>{text}</span>
      ),
    },
    ...permissionColumns.map((action) => ({
      title: action?.title,
      dataIndex: action?.key,
      key: action.key,
      render: (_, __, index) => (
        <Switch
          checked={tableData[index]?.[action?.key]}
          onChange={(checked) =>
            handleSwitchChange(index, action?.key, checked)
          }
          style={{
            backgroundColor: tableData[index]?.[action?.key]
              ? "rgba(57, 181, 74, 1)"
              : undefined,
          }}
        />
      ),
    })),
  ];

  return (
    <CustomDrawer
      title={`${
        userRoleData && Object.keys(userRoleData).length > 0 ? "Update" : "Add"
      } User Role`}
      open={open}
      onClose={onClose}
      loading={loading}
      className={styles.drawer}
    >
      <form onSubmit={handleSubmit(onSubmit)} className={styles.drawer_body}>
        <Row gutter={[16, 16]}>
          <h3 className={styles.heading}>
            <span className={styles.dummy_green_rectangle}></span>
            <span>User Information</span>
          </h3>

          <Col span={12}>
            <Controller
              name="full_name"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="full_name"
                  label="Type Full Name"
                  showRequired
                  labelColor="#667085"
                  placeholder="Type Full name"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <label
              htmlFor="profile_picture"
              style={{
                display: "block",
                marginBottom: 8,
                color: "#667085",
                fontWeight: 600,
              }}
            >
              Profile Picture (jpg, png)
            </label>
            <Controller
              name="profile_picture"
              control={control}
              rules={{ required: false }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Upload
                  beforeUpload={(file) => {
                    setFileName(file.name);
                    onChange(file);
                    return false;
                  }}
                  showUploadList={false}
                  accept=".jpg,.png"
                  status={error ? "error" : ""}
                  className={styles.common_input_styles}
                >
                  <Button
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <span>{fileName || "Select a file to Upload"}</span>
                    <UploadOutlined />
                  </Button>
                </Upload>
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="phone_number"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="phone_number"
                  label="Phone Number"
                  showRequired
                  labelColor="#667085"
                  placeholder="Enter Phone number"
                  size="large"
                  prefix={<PhoneOutlined style={{ transform: "scaleX(-1)" }} />}
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="email_address"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="email_address"
                  label="Email"
                  showRequired
                  labelColor="#667085"
                  placeholder="Enter email"
                  size="large"
                  prefix={<MailOutlined />}
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>
        </Row>

        <h3 className={styles.heading}>
          <span className={styles.dummy_green_rectangle}></span>
          <span>Manage Role:</span>
        </h3>

        <div className={styles.tableContainer}>
          <CustomTable
            columns={columns}
            dataSource={tableData}
            loading={loading}
            showMoreIcon={false}
            showTotalText={false}
            pagination={false}
          />
        </div>
        <div className={styles.footer}>
          <CustomButton
            title={"Submit"}
            type="submit"
            loading={loading}
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </form>
    </CustomDrawer>
  );
};

export default AddEditUserRole;
